import { useState, useEffect } from "react";
import { apiFetch } from "@/lib/api-client";

interface ScoreFactor {
  label: string;
  points: number;
  evidence: string;
}

interface ScoreCategory {
  name: "engagement" | "fit" | "intent";
  score: number;
  maxScore: number;
  factors: ScoreFactor[];
}

export interface ContactScoreBreakdown {
  totalScore: number;
  tier: "Hot" | "Warm" | "Cold";
  categories: ScoreCategory[];
  improvementSuggestion: string;
}

export interface ContactScoreBreakdownMeta {
  degraded?: boolean;
  reason?: string;
}

export function useContactScoreBreakdown(contactId: string | null) {
  const [data, setData] = useState<ContactScoreBreakdown | null>(null);
  const [meta, setMeta] = useState<ContactScoreBreakdownMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contactId) {
      setData(null);
      setMeta(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setMeta(null);

    apiFetch<{ data: ContactScoreBreakdown; meta?: ContactScoreBreakdownMeta }>(`/crm/intelligence/contacts/${contactId}/score-breakdown`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setMeta(res.meta ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setMeta(null);
        setError(err instanceof Error ? err.message : "Failed to load score");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [contactId]);

  return { data, meta, isLoading, error };
}
